import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STATUS = { pending: ["Waiting for review", "bg-amber-100 text-amber-800"], approved: ["Published", "bg-emerald-100 text-emerald-800"], rejected: ["Not published", "bg-gray-100 text-gray-600"], hidden: ["Not currently shown", "bg-gray-100 text-gray-600"] };

function SubmitDialog({ defaultName, onClose, onDone }) {
  const [f, setF] = useState({ name: defaultName || "", place: "", title: "", story: "", quote: "" });
  const [file, setFile] = useState(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const send = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, v));
      fd.append("consent", consent); fd.append("file", file);
      await api.post("/stories/submit", fd);
      toast.success("Thank you! Your story was sent to TMF for review.");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const ok = f.name && f.place && f.title && f.story.length >= 20 && file && consent;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="share-story-dialog">
        <DialogHeader><DialogTitle>Share your TMF story</DialogTitle></DialogHeader>
        <p className="text-sm text-gray-600">Tell us how TMF has helped you. Our team reviews every story before it appears on the website and may lightly edit it for length.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <Input data-testid="share-story-name" placeholder="Name to show" value={f.name} onChange={set("name")} />
          <Input data-testid="share-story-place" placeholder="Town / State (e.g. Yola, Adamawa)" value={f.place} onChange={set("place")} />
        </div>
        <Input data-testid="share-story-title" placeholder="Headline (e.g. My shop is busy again)" value={f.title} onChange={set("title")} />
        <Textarea data-testid="share-story-story" rows={5} placeholder="Your story (at least 20 characters)" value={f.story} onChange={set("story")} />
        <Textarea data-testid="share-story-quote" rows={2} placeholder="A short quote in your own words (optional)" value={f.quote} onChange={set("quote")} />
        <label className="text-sm">Your photo
          <input data-testid="share-story-photo" type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0])} className="block mt-1 text-sm" />
        </label>
        <label className="flex items-start gap-2 text-sm"><Checkbox data-testid="share-story-consent" checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" />I agree that TMF may publish my name, photo and story on its website and social media. Everyone in the photo has agreed too.</label>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="share-story-submit" disabled={!ok || busy} onClick={send} className="bg-emerald-700 hover:bg-emerald-800">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send for review"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const ShareStory = ({ defaultName }) => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const load = useCallback(() => { api.get("/stories/mine").then(({ data }) => setRows(data.stories)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="mt-8 rounded-3xl border border-emerald-100 bg-gradient-to-br from-amber-50 to-white p-6" data-testid="share-story-card">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h3 className="font-display font-bold text-lg text-emerald-950 flex items-center gap-2"><Sparkles className="w-5 h-5 text-amber-500" />Share your TMF story</h3>
          <p className="text-sm text-gray-600 mt-1">Your story could inspire sponsors to support more families. Approved stories appear on the TMF homepage.</p>
        </div>
        <Button data-testid="share-story-btn" onClick={() => setOpen(true)} className="rounded-full bg-emerald-700 hover:bg-emerald-800">Share my story</Button>
      </div>
      {rows.length > 0 && (
        <div className="mt-4 divide-y divide-emerald-100" data-testid="my-stories-list">
          {rows.map((s) => {
            const [t, cls] = STATUS[s.review_status] || STATUS.pending;
            return (
              <div key={s.id} className="py-2 flex items-center justify-between gap-3 text-sm" data-testid={`my-story-${s.id}`}>
                <span className="min-w-0"><span className="block truncate">{s.title}</span>{s.review_status === "rejected" && s.review_note && <span className="block text-xs text-gray-500 break-words">TMF note: {s.review_note}</span>}</span>
                <Badge className={cls}>{t}</Badge>
              </div>
            );
          })}
        </div>
      )}
      {open && <SubmitDialog defaultName={defaultName} onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); }} />}
    </div>
  );
};
